/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/dishes.ts
 * 菜品库 API 服务（简约版）
 */

import { httpClient } from './http'
import type { PaginatedResponse } from '../types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function isFile(value: unknown): value is File {
  return typeof File !== 'undefined' && value instanceof File
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

// 轻量菜品类型（满足卡片展示所需字段）
export interface DishItem {
  /**
   * 后端契约为 integer；前端内部统一按 string 使用（通过 normalizeDish 转换）。
   */
  id: string | number
  name: string
  /**
   * UI 使用 number（el-input-number 要求）；后端契约为 decimal string。
   */
  price: number
  /**
   * UI 用库存字段；后端契约字段为 daily_inventory。
   */
  stock?: number
  image_url?: string
  /**
   * UI 兼容字段：菜系选择器的值（历史字段名 cuisine_id）。
   * 后端契约字段：cuisine(int|null)。
   */
  cuisine_id?: string | number | null
  cuisine_label?: string
  /**
   * 运营端创建菜品时需要指定 merchant(int)；商户端由后端自动设置
   */
  merchant?: string | number
  /**
   * 后端契约字段：category(int)
   */
  category?: string | number
  /**
   * 后端契约字段：cuisine(int|null)
   */
  cuisine?: string | number | null
  /**
   * 后端契约字段：daily_inventory(int)
   */
  daily_inventory?: number
  /**
   * 后端契约字段：composition(JSON)
   */
  composition?: unknown
  restaurant?: string | number | null
  merchant_restaurant_id?: string
  // 兼容：部分后端/环境使用 restaurant_id
  restaurant_id?: string
  merchant_name?: string
  restaurant_name?: string
  category_name?: string
  cuisine_name?: string
  /**
   * 后端唯一真源：AVAILABLE（可售）/ UNAVAILABLE（停售）。
   * 数据库无 ON/OFF 历史值（已验证 4300 条均为 AVAILABLE/UNAVAILABLE）。
   */
  status?: 'AVAILABLE' | 'UNAVAILABLE' | string
  // UI 用数值（0-3），来自后端 spiciness_level 的 code 映射
  spicy_level?: number | null
  // 后端真实字段（枚举 code）
  spiciness_level_code?: 'NONE' | 'MILD' | 'MEDIUM' | 'HOT'
  // 新增：菜品类型分组与代码（前端用，可选）
  dish_type_group?: 'combo' | 'single' | 'materials'
  dish_type_code?: string
  weight_text?: string | null
  description?: string | null
  pairing_text?: string | null
  sales_total?: number | null
  rating_score?: number | null
  taste_score?: number | null
  tags?: string | string[] | null
  cabinet_suitability?: string | null
  dish_size?: 'XL' | 'LARGE' | 'MEDIUM' | 'SMALL' | string | null
  cabinet?: string
  note?: string
  display_order?: number
  created_at?: string
  updated_at?: string
  // 组成明细（主菜/副菜/主食/水果/饮料/配汤）
  compositions?: Array<{
    type: 'main' | 'side' | 'staple' | 'fruit' | 'drink' | 'soup'
    name: string
    weight_g?: number
    weight_text?: string
    ingredients?: string[]
  }>
  // 规格配置（不影响价格，单选）
  specifications?: Array<{
    name: string
    required?: boolean
    options: string[]
  }> | null
}

export interface DishFilters {
  keyword?: string
  category?: string
  price_range?: string
  cabinet_suitability?: string
  dish_size?: string
  cuisine_id?: string
  merchant_id?: string
  merchant_restaurant_id?: string
  restaurant_id?: string
  ordering?: string
  page?: number
  page_size?: number
}

/**
 * 菜品写入载荷（契约口径，schema.yml: DishRequest/PatchedDishRequest）
 */
export interface DishWritePayload {
  merchant?: number
  restaurant?: number | null
  category: number
  cuisine?: number | null
  cuisine_code?: string | null
  name: string
  price: string | number
  description?: string | null
  daily_inventory?: number | null
  display_order?: number | null
  tags?: string[] | null
  cabinet_suitability?: string | null
  dish_size?: 'XL' | 'LARGE' | 'MEDIUM' | 'SMALL' | string | null
  composition?: unknown
  spiciness_level?: 'NONE' | 'MILD' | 'MEDIUM' | 'HOT' | string | null
  /**
   * 菜品图片
   * - create/update：File（multipart/form-data 上传）
   * - update：null（JSON patch 清空）
   */
  image?: File | null
}

export interface PaginatedDishResponse extends PaginatedResponse<DishItem> {
  page: number
  page_size: number
  total_pages: number
}

class DishApiService {
  private readonly endpoint = 'merchants/dishes/'
  private readonly SPICY_CODE_TO_VALUE: Record<string, number> = { NONE: 0, MILD: 1, MEDIUM: 2, HOT: 3 }
  private readonly SPICY_VALUE_TO_CODE: Record<number, 'NONE'|'MILD'|'MEDIUM'|'HOT'> = { 0: 'NONE', 1: 'MILD', 2: 'MEDIUM', 3: 'HOT' }
  private createSvgPlaceholder(text?: string): string {
    const label = (text || 'Dish').slice(0, 8)
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'>
  <rect width='100%' height='100%' fill='%23f2f3f5'/>
  <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-size='28' fill='%239aa0a6'>${label}</text>
</svg>`
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
  }

  /**
   * 加载全部菜品（自动分页循环，适用于菜单/排期编辑器需要展示所有可选菜品的场景）
   * 每页请求 page_size=100（后端最大值），循环加载直到所有页加载完毕。
   */
  async listAll(params?: Omit<DishFilters, 'page' | 'page_size'>): Promise<DishItem[]> {
    const pageSize = 100
    const allItems: DishItem[] = []
    let page = 1
    let hasMore = true

    while (hasMore) {
      const resp = await this.list({ ...params, page, page_size: pageSize })
      allItems.push(...resp.results)
      // 判断是否还有下一页
      hasMore = resp.results.length >= pageSize && allItems.length < resp.count
      page++
      // 安全上限：最多 50 页（5000 个菜品）
      if (page > 50) break
    }
    return allItems
  }

  async list(params?: DishFilters): Promise<PaginatedDishResponse> {
    try {
      // 兼容两种响应：{ data: { count, results... } } 与 DRF 原生 { count, results... }
      const axios = httpClient.getAxiosInstance()
      const resp = await axios.get<PaginatedDishResponse | { data: PaginatedDishResponse }>(this.endpoint, { params })
      const raw: unknown = resp.data
      const normalized: unknown = (() => {
        if (isRecord(raw) && Array.isArray(raw.results)) return raw
        if (isRecord(raw) && isRecord(raw.data) && Array.isArray(raw.data.results)) return raw.data
        return raw
      })()

      if (!isRecord(normalized) || !Array.isArray(normalized.results)) {
        throw new Error('Invalid dish list response shape')
      }
      const results = normalized.results as unknown[]

      // 统一补齐分页字段（若后端未提供 page/page_size/total_pages）
      const page = params?.page || 1
      const pageSize = params?.page_size || 20
      const count = typeof normalized.count === 'number' ? normalized.count : results.length
      const totalPages = Math.max(1, Math.ceil(count / pageSize))

      return {
        results: results.map((it) => this.normalizeDish(this.normalizeSpiciness(it))),
        count,
        page,
        page_size: pageSize,
        total_pages: typeof normalized.total_pages === 'number' ? normalized.total_pages : totalPages,
        next: typeof normalized.next === 'string' ? normalized.next : null,
        previous: typeof normalized.previous === 'string' ? normalized.previous : null,
      }
    } catch (error) {
      // 开发阶段使用 Mock 兜底（生产环境禁止 fallback mock）
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockDishes(params)
      }
      throw error
    }
  }

  async getById(id: string): Promise<DishItem> {
    try {
      const axios = httpClient.getAxiosInstance()
      const resp = await axios.get<DishItem | { data: DishItem }>(`${this.endpoint}${id}/`)
      const raw: unknown = resp.data
      const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
      const item: DishItem = this.normalizeDish(this.normalizeSpiciness(nested))
      if (!item || !item.id) {
        throw new Error('Invalid dish detail response shape')
      }
      return item
    } catch (error) {
      // 开发阶段使用 Mock 兜底（生产环境禁止 fallback mock）
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') {
        return this.getMockDishById(id)
      }
      throw error
    }
  }

  private normalizeSpiciness(item: unknown): DishItem {
    const it = toRecord(item)
    const code = typeof it.spiciness_level === 'string'
      ? it.spiciness_level
      : (typeof it.spiciness_level_code === 'string' ? it.spiciness_level_code : undefined)
    const out: Record<string, unknown> = { ...it }
    if (typeof code === 'string') {
      out.spicy_level = this.SPICY_CODE_TO_VALUE[code] ?? 0
      out.spiciness_level_code = code
      return out as unknown as DishItem
    }
    if (typeof it.spicy_level === 'number' && it.spiciness_level_code == null) {
      out.spiciness_level_code = this.SPICY_VALUE_TO_CODE[it.spicy_level] ?? 'NONE'
    }
    return out as unknown as DishItem
  }

  private normalizeDish(item: DishItem): DishItem {
    const it = toRecord(item)
    const out: Record<string, unknown> = { ...it }

    // id: 后端 integer -> 前端 string（避免路由/选择器/Map key 混用 number/string）
    if (typeof it.id === 'number') out.id = String(it.id)

    // price: 后端 Decimal 字符串 -> UI number
    if (typeof it.price === 'string') {
      const n = parseFloat(it.price)
      out.price = Number.isFinite(n) ? n : 0
    }

    // status: 保留后端原始枚举值 AVAILABLE/UNAVAILABLE（不再转换为 ON/OFF）
    if (it.status === 'AVAILABLE' || it.status === 'UNAVAILABLE') {
      out.status = it.status
    }

    // image_url: 兼容后端仅返回 image（ImageField.url 字符串）的场景
    if (typeof it.image_url !== 'string') {
      const img = (it as unknown as Record<string, unknown>).image
      if (typeof img === 'string' && img.trim()) out.image_url = img
    }

    // daily_inventory -> stock（兼容旧 UI 字段名）
    if (typeof it.daily_inventory === 'number' && it.stock == null) {
      out.stock = it.daily_inventory
    }

    // cuisine -> cuisine_id（兼容旧 UI 字段名）
    if (typeof it.cuisine === 'number' && it.cuisine_id == null) {
      out.cuisine_id = it.cuisine
    }

    return out as unknown as DishItem
  }

  async delete(id: string): Promise<void> {
    try {
      await httpClient.delete(`${this.endpoint}${id}/`)
    } catch (error) {
      // 允许无后端时静默通过（仅限开发环境）
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') return
      throw error
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`${this.endpoint}${id}/`)
    } catch (error) {
      // 允许无后端时静默通过（仅限开发环境）
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') return
      throw error
    }
  }

  async offShelf(id: string): Promise<void> {
    try {
      await httpClient.post(`${this.endpoint}${id}/off_shelf/`)
    } catch (error) {
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') return
      throw error
    }
  }

  async onShelf(id: string): Promise<void> {
    try {
      await httpClient.post(`${this.endpoint}${id}/on_shelf/`)
    } catch (error) {
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') return
      throw error
    }
  }

  async update(id: string, data: Partial<DishItem> | DishWritePayload): Promise<DishItem> {
    try {
      const axios = httpClient.getAxiosInstance()
      const payload = { ...toRecord(this.transformPayload(data)) }

      // 将数值 spicy_level 转为枚举 code 字段 spiciness_level
      if (typeof payload.spicy_level === 'number') {
        payload.spiciness_level = this.SPICY_VALUE_TO_CODE[payload.spicy_level] ?? 'NONE'
        delete payload.spicy_level
      }

      // 清空图片：使用 JSON patch（multipart 下清空语义不稳定）
      if ('image' in payload && payload.image === null) {
        const resp = await axios.patch<DishItem | { data: DishItem }>(`${this.endpoint}${id}/`, { image: null })
        const raw: unknown = resp.data
        const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
        const item: DishItem = nested as DishItem
        if (!item || !item.id) throw new Error('Invalid dish update response shape')
        return item
      }

      // 上传图片：包含 File 时改用 multipart/form-data
      const useMultipart = isFile(payload.image)
      const resp = useMultipart
        ? await axios.patch<DishItem | { data: DishItem }>(`${this.endpoint}${id}/`, this.toFormData(payload))
        : await axios.patch<DishItem | { data: DishItem }>(`${this.endpoint}${id}/`, payload)
      const raw: unknown = resp.data
      const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
      const item: DishItem = nested as DishItem
      if (!item || !item.id) {
        throw new Error('Invalid dish update response shape')
      }
      return item
    } catch (error) {
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') {
        const src = toRecord(data)
        const priceNum = (() => {
          const raw = src.price
          if (typeof raw === 'number') return raw
          if (typeof raw === 'string') return parseFloat(raw) || 0
          return 0
        })()
        // 模拟返回合并后的结果
        return {
          id,
          name: typeof src.name === 'string' ? src.name : '未命名菜品',
          price: priceNum,
          image_url: typeof src.image_url === 'string' ? src.image_url : undefined,
          cuisine_id: (src.cuisine_id ?? src.cuisine) as (string | number | null | undefined),
          cuisine_label: typeof src.cuisine_label === 'string' ? src.cuisine_label : '—',
          merchant_name: '模拟商户',
          restaurant_name: '模拟餐厅',
          status: 'ON',
          spicy_level: typeof src.spicy_level === 'number' ? src.spicy_level : 0,
          weight_text: typeof src.weight_text === 'string' ? src.weight_text : '—',
          description: typeof src.description === 'string' ? src.description : null,
          pairing_text: typeof src.pairing_text === 'string' ? src.pairing_text : null,
          dish_type_group: src.dish_type_group as (DishItem['dish_type_group'] | undefined),
          dish_type_code: typeof src.dish_type_code === 'string' ? src.dish_type_code : undefined,
          sales_total: 0,
          rating_score: 4.5,
          taste_score: 4.2,
          updated_at: new Date().toISOString(),
        }
      }
      throw error
    }
  }

  async create(data: Partial<DishItem> | DishWritePayload): Promise<DishItem> {
    try {
      const axios = httpClient.getAxiosInstance()
      const payload = { ...toRecord(this.transformPayload(data)) }
      if (typeof payload.spicy_level === 'number') {
        payload.spiciness_level = this.SPICY_VALUE_TO_CODE[payload.spicy_level] ?? 'NONE'
        delete payload.spicy_level
      }
      const useMultipart = isFile(payload.image)
      const resp = useMultipart
        ? await axios.post<DishItem | { data: DishItem }>(this.endpoint, this.toFormData(payload))
        : await axios.post<DishItem | { data: DishItem }>(this.endpoint, payload)
      const raw: unknown = resp.data
      const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
      const item: DishItem = nested as DishItem
      if (!item || !item.id) {
        throw new Error('Invalid dish create response shape')
      }
      return item
    } catch (error) {
      if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK !== 'false') {
        const src = toRecord(data)
        const priceNum = (() => {
          const raw = src.price
          if (typeof raw === 'number') return raw
          if (typeof raw === 'string') return parseFloat(raw) || 0
          return 0
        })()
        return {
          id: `dish_${Date.now()}`,
          name: typeof src.name === 'string' ? src.name : '未命名菜品',
          price: priceNum,
          image_url: typeof src.image_url === 'string' ? src.image_url : undefined,
          cuisine_id: (src.cuisine_id ?? src.cuisine) as (string | number | null | undefined),
          cuisine_label: typeof src.cuisine_label === 'string' ? src.cuisine_label : '—',
          merchant_name: '模拟商户',
          restaurant_name: '模拟餐厅',
          status: 'ON',
          spicy_level: typeof src.spicy_level === 'number' ? src.spicy_level : 0,
          weight_text: typeof src.weight_text === 'string' ? src.weight_text : '—',
          pairing_text: typeof src.pairing_text === 'string' ? src.pairing_text : null,
          description: typeof src.description === 'string' ? src.description : null,
          dish_type_group: src.dish_type_group as (DishItem['dish_type_group'] | undefined),
          dish_type_code: typeof src.dish_type_code === 'string' ? src.dish_type_code : undefined,
          sales_total: 0,
          rating_score: 4.5,
          taste_score: 4.2,
          updated_at: new Date().toISOString(),
        }
      }
      throw error
    }
  }

  // 发送载荷转换：只发送后端契约字段（schema.yml: DishRequest），并做字段名/类型兼容转换
  private transformPayload(data: Partial<DishItem> | DishWritePayload): Record<string, unknown> {
    const src = toRecord(data)
    const payload: Record<string, unknown> = {}

    // name
    if (typeof src.name === 'string') payload.name = src.name

    // merchant（运营端创建需要）
    if (typeof src.merchant === 'number') payload.merchant = src.merchant
    if (typeof src.merchant === 'string' && src.merchant.trim() && /^\d+$/.test(src.merchant.trim())) {
      payload.merchant = parseInt(src.merchant.trim(), 10)
    }

    // restaurant（创建时可传，后端也会自动从 merchant 推导）
    const restaurantRaw = (src.restaurant ?? src.merchant_restaurant_id) as unknown
    if (typeof restaurantRaw === 'number') payload.restaurant = restaurantRaw
    if (typeof restaurantRaw === 'string' && restaurantRaw.trim() && /^\d+$/.test(restaurantRaw.trim())) {
      payload.restaurant = parseInt(restaurantRaw.trim(), 10)
    }

    // category（必填，DishCategory 主键）
    if (typeof src.category === 'number') payload.category = src.category
    if (typeof src.category === 'string' && src.category.trim() && /^\d+$/.test(src.category.trim())) {
      payload.category = parseInt(src.category.trim(), 10)
    }

    // cuisine（可选）
    const cuisineRaw = (src.cuisine ?? src.cuisine_id) as unknown
    if (typeof cuisineRaw === 'number') payload.cuisine = cuisineRaw
    if (typeof cuisineRaw === 'string' && cuisineRaw.trim() && /^\d+$/.test(cuisineRaw.trim())) {
      payload.cuisine = parseInt(cuisineRaw.trim(), 10)
    }
    // cuisine_code（契约 writeOnly；当 cuisine 缺失或常量缺少 id 时用于写入）
    if (payload.cuisine == null && typeof (src as Record<string, unknown>).cuisine_code === 'string') {
      const code = String((src as Record<string, unknown>).cuisine_code || '').trim()
      if (code) payload.cuisine_code = code
    }

    // price（后端契约为 decimal string）
    const price = src.price
    if (typeof price === 'number') payload.price = Number.isFinite(price) ? price.toFixed(2) : '0.00'
    if (typeof price === 'string' && price.trim()) payload.price = price.trim()

    // description
    if (typeof src.description === 'string') payload.description = src.description

    // image（File | null）
    if ('image' in src && isFile(src.image)) payload.image = src.image
    if ('image' in src && src.image === null) payload.image = null

    // daily_inventory（兼容旧字段 stock）
    const inv = (src.daily_inventory ?? src.stock) as unknown
    if (typeof inv === 'number') payload.daily_inventory = inv
    if (typeof inv === 'string' && inv.trim() && /^\d+$/.test(inv.trim())) {
      payload.daily_inventory = parseInt(inv.trim(), 10)
    }

    // display_order
    if (typeof src.display_order === 'number') payload.display_order = src.display_order

    // tags（兼容旧单值字符串）
    const tags = src.tags as unknown
    if (Array.isArray(tags)) payload.tags = tags
    if (typeof tags === 'string') payload.tags = tags.trim() ? [tags.trim()] : []

    // cabinet_suitability：若页面已按契约传入，则直接透传
    if (typeof (src as Record<string, unknown>).cabinet_suitability === 'string') {
      payload.cabinet_suitability = (src as Record<string, unknown>).cabinet_suitability
    }
    // cabinet_suitability（兼容旧字段 cabinet: heating/warming/cooling）
    if (payload.cabinet_suitability == null && typeof src.cabinet === 'string' && src.cabinet.trim()) {
      const map: Record<string, string> = { heating: 'HEATED', warming: 'NORMAL', cooling: 'NORMAL' }
      payload.cabinet_suitability = map[src.cabinet.trim()] ?? 'NOT_SUITABLE'
    }

    // composition（兼容旧字段 compositions）
    if ('composition' in src) payload.composition = src.composition
    if (!('composition' in src) && 'compositions' in src) payload.composition = (src as Record<string, unknown>).compositions

    // status：前端传入 AVAILABLE/UNAVAILABLE，直接透传
    if (typeof (src as Record<string, unknown>).status === 'string') {
      const st = String((src as Record<string, unknown>).status).trim()
      if (st === 'AVAILABLE' || st === 'UNAVAILABLE') {
        payload.status = st
      }
    }

    // spiciness_level：若页面已按契约传入，则直接透传（否则上层会用 spicy_level(number) 映射）
    if (typeof (src as Record<string, unknown>).spiciness_level === 'string') {
      payload.spiciness_level = (src as Record<string, unknown>).spiciness_level
    }

    // dish_size：若页面已按契约传入，则直接透传
    if (typeof (src as Record<string, unknown>).dish_size === 'string') {
      payload.dish_size = (src as Record<string, unknown>).dish_size
    }

    // specifications（规格配置 JSONField）
    if ('specifications' in src) {
      const specs = src.specifications
      if (Array.isArray(specs)) {
        payload.specifications = specs.length > 0 ? specs : null
      } else if (specs === null || specs === undefined) {
        payload.specifications = null
      }
    }

    // ⚠️ 不提交：merchant_restaurant_id/restaurant_id/dish_type_code/dish_type_group 等 UI 历史字段
    return payload
  }

  /**
   * 将 payload 转为 multipart/form-data（用于图片上传）
   * - 复杂对象字段（如 composition）统一 JSON.stringify
   * - 数组字段用“重复 key”形式 append
   */
  private toFormData(payload: Record<string, unknown>): FormData {
    const fd = new FormData()
    // 后端将以下字段按 JSONField 解析（multipart 下必须传 JSON 字符串，而不是重复 key）
    const jsonFieldKeys = new Set<string>(['composition', 'tags', 'specifications'])
    for (const [key, val] of Object.entries(payload)) {
      if (val === undefined || val === null) continue
      if (isFile(val)) {
        fd.append(key, val)
        continue
      }
      if (jsonFieldKeys.has(key)) {
        // 已是字符串时直接透传（兼容某些页面已预先 JSON.stringify）
        fd.append(key, typeof val === 'string' ? val : JSON.stringify(val))
        continue
      }
      if (Array.isArray(val)) {
        for (const it of val) {
          if (it === undefined || it === null) continue
          fd.append(key, String(it))
        }
        continue
      }
      if (typeof val === 'object') {
        fd.append(key, JSON.stringify(val))
        continue
      }
      fd.append(key, String(val))
    }
    return fd
  }

  // =============== Mock ===============
  private async getMockDishes(params?: DishFilters): Promise<PaginatedDishResponse> {
    await new Promise(r => setTimeout(r, 220))
    // Mock 需尽量贴近后端契约字段（避免开发/测试阶段误判）
    const pool: Array<Record<string, unknown>> = Array.from({ length: 36 }).map((_, idx) => ({
      id: idx + 1,
      name: ['宫保鸡丁', '麻婆豆腐', '白米饭', '酸辣土豆丝', '烤鸡排', '红烧茄子'][idx % 6] + (idx > 5 ? idx : ''),
      // 后端契约：decimal string
      price: String([28, 22, 3, 12, 18, 16][idx % 6].toFixed(2)),
      image_url: this.createSvgPlaceholder(['宫保鸡丁', '麻婆豆腐', '白米饭', '酸辣土豆丝', '烤鸡排', '红烧茄子'][idx % 6]),
      // 后端契约：cuisine(int|null)
      cuisine: [4, 5, 1][idx % 3],
      cuisine_label: ['川湘菜系', '粤式菜系', '中式快餐'][idx % 3],
      // 后端契约：category(int)（mock 用占位 id；仅用于页面筛选/联调）
      category: (idx % 3) + 1,
      // 后端契约：daily_inventory(int)
      daily_inventory: 50 + (idx * 7) % 200,
      merchant_restaurant_id: ['1', '2', '3', '4'][idx % 4],
      merchant_name: ['川香餐厅', '粤式茶餐厅', '家常小馆'][idx % 3],
      restaurant_name: ['总店', '天河店', '朝阳店'][idx % 3],
      // 后端契约口径：AVAILABLE/UNAVAILABLE
      status: idx % 7 === 0 ? 'UNAVAILABLE' : 'AVAILABLE',
      // 后端契约口径：spiciness_level 枚举 code
      spiciness_level: (['NONE', 'MILD', 'MEDIUM', 'HOT'] as const)[idx % 4],
      weight_text: ['350g', '400g', '500g'][idx % 3],
      pairing_text: ['主菜+副菜+青菜+主食', '干锅+主食', '配汤+主食'][idx % 3] + `，共${[350,400,500][idx%3]}g`,
      description: '香酥微辣，外焦里嫩，营养均衡',
      sales_total: 50 + (idx * 7) % 500,
      rating_score: [4.2, 4.5, 4.7, 4.0][idx % 4],
      taste_score: [3.8, 4.0, 4.3, 4.5][idx % 4],
      updated_at: new Date().toISOString()
    }))

    let list = pool
    if (params?.keyword) {
      const kw = params.keyword.toLowerCase()
      list = list.filter(d => String(d.name || '').toLowerCase().includes(kw) || String(d.merchant_name || '').toLowerCase().includes(kw))
    }
    if (params?.cuisine_id) list = list.filter(d => String(d.cuisine || '') === String(params.cuisine_id))
    if (params?.merchant_restaurant_id) list = list.filter(d => String(d.merchant_restaurant_id || '') === String(params.merchant_restaurant_id))

    const page = params?.page || 1
    const pageSize = params?.page_size || 20
    const start = (page - 1) * pageSize
    const end = start + pageSize

    return {
      results: list.slice(start, end).map((it) => this.normalizeDish(this.normalizeSpiciness(it))),
      count: list.length,
      page,
      page_size: pageSize,
      total_pages: Math.ceil(list.length / pageSize),
      next: end < list.length ? `page=${page + 1}` : null,
      previous: page > 1 ? `page=${page - 1}` : null
    }
  }

  private async getMockDishById(id: string): Promise<DishItem> {
    await new Promise(r => setTimeout(r, 150))
    
    // 从ID中提取数字索引（兼容 "1" / "dish_1" 两种形式）
    const normalizedId = String(id || '')
    const idx = parseInt(normalizedId.replace('dish_', ''), 10) - 1
    if (idx < 0) {
      throw new Error('菜品不存在')
    }

    // 生成详细的菜品信息
    const dishNames = ['宫保鸡丁', '麻婆豆腐', '白米饭', '酸辣土豆丝', '烤鸡排', '红烧茄子']
    const baseName = dishNames[idx % 6]
    const displayName = baseName + (idx > 5 ? (idx + 1) : '')

    return {
      id,
      name: displayName,
      price: [28, 22, 3, 12, 18, 16][idx % 6],
      stock: 50 + (idx * 13) % 200,
      image_url: this.createSvgPlaceholder(displayName),
      cuisine_id: ['cuisine_004', 'cuisine_005', 'cuisine_001'][idx % 3],
      cuisine_label: ['川湘菜系', '粤式菜系', '中式快餐'][idx % 3],
      merchant_name: ['川香餐厅', '粤式茶餐厅', '家常小馆'][idx % 3],
      restaurant_name: ['三里屯店', '天河店', '朝阳店'][idx % 3],
      status: idx % 7 === 0 ? 'OFF' : 'ON',
      spicy_level: [0, 1, 2, 3][idx % 4],
      weight_text: ['350g', '400g', '500g'][idx % 3],
      pairing_text: ['主菜+副菜+青菜+主食', '干锅+主食', '配汤+主食'][idx % 3],
      description: `${baseName}是一道经典的传统菜品，选用优质食材，经过精心烹制而成。口感丰富，营养均衡，深受广大食客喜爱。适合各种场合享用，是您用餐的不二选择。`,
      category: ['chinese', 'buffet', 'fast'][idx % 3],
      tags: ['signature', 'new', 'hot'][idx % 3],
      cabinet: ['heating', 'warming', 'cooling'][idx % 3],
      note: idx % 4 === 0 ? '特别推荐，适合大众口味' : '',
      display_order: idx % 20,
      sales_total: 50 + (idx * 7) % 500,
      rating_score: [4.2, 4.5, 4.7, 4.0][idx % 4],
      taste_score: [3.8, 4.0, 4.3, 4.5][idx % 4],
      created_at: new Date(Date.now() - (idx * 86400000)).toISOString(), // 递减创建时间
      updated_at: new Date(Date.now() - (idx * 3600000)).toISOString(), // 递减更新时间
      compositions: idx % 3 === 0 ? [
        {
          type: 'main' as const,
          name: '主菜',
          weight_g: 120,
          weight_text: '120g',
          ingredients: ['鸡肉', '花生米']
        },
        {
          type: 'side' as const,
          name: '配菜',
          weight_g: 80,
          weight_text: '80g',
          ingredients: ['青椒', '胡萝卜']
        },
        {
          type: 'staple' as const,
          name: '主食',
          weight_g: 150,
          weight_text: '150g',
          ingredients: ['米饭']
        }
      ] : undefined,
      specifications: idx % 4 === 0 ? [
        { name: '温度', required: true, options: ['热', '常温', '去冰'] },
        { name: '份量', required: true, options: ['标准', '大份'] },
      ] : undefined
    }
  }
}

export const dishApi = new DishApiService()
// 类型已在本文件内声明并导出，无需重复导出聚合


